/**
 * ConfirmDeleteChatDialog — confirmación explícita antes de borrar un chat.
 *
 * US-467: evita borrados accidentales desde el rail. El borrado es IRREVERSIBLE
 * (el kernel purga el chat del store R0), así que exigimos una confirmación
 * humana explícita — este diálogo es la puerta que gatea el `approve`.
 *
 * Presentacional: NO llama al kernel. Captura la decisión (Eliminar/Cancelar) y
 * la delega al padre (AppShell), que orquesta propose → approve → refresh. El
 * estado en vuelo (`busy`) y el error (`error`) los controla el padre.
 *
 * Accesible: role="dialog" + aria-modal, etiquetado por título, foco inicial en
 * "Cancelar" (acción segura para un destructivo), trampa de foco entre los dos
 * botones, Escape y clic en el fondo cancelan (salvo mientras borra).
 */

import { useEffect, useRef } from 'react'
import { Trash2, OctagonAlert } from 'lucide-react'

export default function ConfirmDeleteChatDialog({ chat, busy = false, error = null, onConfirm, onCancel }) {
  const cancelRef = useRef(null)
  const confirmRef = useRef(null)

  // Foco inicial en "Cancelar": para un destructivo, el foco por defecto va a
  // la acción segura (Enter no borra por accidente).
  useEffect(() => {
    cancelRef.current?.focus()
  }, [])

  // Escape cancela (salvo en vuelo). Trampa de foco simple entre los dos
  // botones para que Tab no salga del diálogo.
  function onKeyDown(e) {
    if (e.key === 'Escape' && !busy) {
      e.stopPropagation()
      onCancel?.()
      return
    }
    if (e.key === 'Tab') {
      const first = cancelRef.current
      const last = confirmRef.current
      if (!first || !last) return
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
  }

  const titulo = chat?.titulo ?? 'esta conversación'

  return (
    <div
      className="modal-backdrop"
      // Clic en el fondo (no en el diálogo) cancela, salvo mientras borra.
      onMouseDown={e => { if (e.target === e.currentTarget && !busy) onCancel?.() }}
    >
      <div
        className="modal modal--danger"
        role="dialog"
        aria-modal="true"
        aria-labelledby="del-chat-title"
        aria-describedby="del-chat-desc"
        onKeyDown={onKeyDown}
      >
        <div className="modal__header">
          <span className="modal__icon modal__icon--danger" aria-hidden="true">
            <Trash2 size={18} strokeWidth={1.9} />
          </span>
          <h2 className="modal__title" id="del-chat-title">Eliminar conversación</h2>
        </div>

        <div className="modal__body" id="del-chat-desc">
          <p>
            ¿Seguro que quieres eliminar <strong>«{titulo}»</strong>?
          </p>
          <p className="modal__warning">
            <OctagonAlert size={14} strokeWidth={2} aria-hidden="true" />
            Esta acción es permanente: la conversación se borrará de tu historial y no se puede deshacer.
          </p>
          {error && <p className="modal__error" role="alert">{error}</p>}
        </div>

        <div className="modal__actions">
          <button
            ref={cancelRef}
            type="button"
            className="btn btn--ghost"
            onClick={onCancel}
            disabled={busy}
          >
            Cancelar
          </button>
          <button
            ref={confirmRef}
            type="button"
            className="btn btn--danger"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? 'Eliminando…' : 'Eliminar'}
          </button>
        </div>
      </div>
    </div>
  )
}
